<template>
	<div
		@drop.prevent="handleDrop"
		@dragenter.prevent="setActive"
		@dragover.prevent="setActive"
		@dragleave.prevent="setInactive"
		:class="{ 'tw-rounded-lg tw-outline tw-outline-blue-500': active }"
	>
		<AppInput
			:modelValue="fileName"
			:label="label"
			:placeholder="fileName ?? placeholder"
			:size="size"
			:hint="hint"
			:rules="rules"
			disabled
		>
			<template #start-icon>
				<a
					v-if="fileName && !disablePreview"
					:href="fileName"
					target="_blank"
					class="tw-absolute tw-start-0 tw-top-0 tw-flex tw-h-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-bg-white tw-p-1"
				>
					<AppImage
						:src="fileName"
						:alt="'test'"
						class="tw-max-h-full tw-w-14 tw-rounded-md tw-object-contain tw-transition-all tw-ease-in hover:tw-scale-105"
					/>
				</a>
			</template>
			<template #end-btn>
				<AppButton
					:startIcon="fileName ? closeIcon : btnIcon"
					tertiary
					variant="gray"
					:label="fileName ? '' : btnLabel"
					:size="btnSize"
					@click="fileName ? reset() : $refs.inputEl.click()"
					:loading="loading"
					type="button"
				/>
			</template>
			<template #hint>
				<slot name="hint"></slot>
			</template>
		</AppInput>
		<input
			type="file"
			ref="inputEl"
			v-show="false"
			@change="handleFileChange"
			:accept="acceptTypes"
		/>
	</div>
</template>

<script setup>
import { ref, watch } from 'vue';
import {
	uploadToFireStore,
	uploadToS3,
	isFileValid,
	getFileExt,
	compressImageFile,
	compressVideoFile,
} from '@/v2/utils/file-upload';
import { useAuthStore } from '@/v2/store/shared/auth-store';
import { Alert } from '@/v2/plugins/alert';
import { t } from '@/locales/index';

const props = defineProps({
	modelValue: {},
	uploadType: {
		type: String,
		default: 'firestore', // firestore, s3, without-upload
	},
	label: {
		type: String,
		default: '',
	},
	placeholder: {
		type: String,
		default: '',
	},
	hint: {
		type: String,
		default: '',
	},
	size: {
		type: String,
		default: 'md',
	},
	btnSize: {
		type: String,
		default: 'xl',
	},
	btnIcon: {
		type: String,
		default: 'assets/v2/image/icons/upload.svg',
	},
	btnLabel: {
		type: String,
		default: '',
	},
	acceptTypes: {
		type: String,
		default: 'image/*',
	},
	rules: {
		type: Array,
		default: () => [],
	},
	allowedExts: {
		type: Array,
		default: () => ['jpg', 'jpeg', 'png', 'svg', 'webp'],
	},
	shortPath: {
		type: Boolean,
		default: false,
	},
	firestorePath: {
		type: [String, Function],
		default: 'images',
	},
	maxSize: {
		type: [Number, Function], // MB
		default: undefined,
	},
	disablePreview: {
		type: Boolean,
		default: false,
	},
	disableCompression: {
		type: Boolean,
		default: false,
	},
});

const loading = ref(false);
const fileName = ref(props.modelValue || null);
const authStore = useAuthStore();
const emit = defineEmits(['update:modelValue', 'data']);
const closeIcon = 'assets/v2/image/icons/close.svg';
const active = ref(false);

function setActive() {
	active.value = true;
}
function setInactive() {
	active.value = false;
}

const handleFileChange = async (event) => {
	const files = event.target.files;
	if (!files || !files.length) {
		reset();
		return;
	}
	const file = files[0];

	handleUploadFile(file);
};

const handleUploadFile = async (originalFile) => {
	if (!isFileValid(originalFile.name, props.allowedExts)) {
		Alert.error(t('unsupported-file-with-ext', { exts: props.allowedExts.join(', ') }));
		return;
	}

	let file = originalFile;

	if (!props.disableCompression) {
		try {
			loading.value = true;
			if (file.type?.includes('image')) {
				file = await compressImageFile(file);
			} else if (file.type?.includes('video')) {
				file = await compressVideoFile(file);
			}
		} finally {
			loading.value = false;
		}
	}

	const now = new Date();
	const fileRef = `${now.getTime() + String(Math.random())}`;
	const path =
		typeof props.firestorePath === 'string'
			? `tenants/${authStore.user?.tenant?.id}/${props.firestorePath}/${fileRef}`
			: props.firestorePath({ file, ext: getFileExt(file.name) });

	const mbSize = Math.ceil(file?.size / (1000 * 1000));
	let maxSize = props.maxSize;
	if (maxSize === undefined && props.uploadType === 'firestore') {
		if (file.type.includes('image')) {
			maxSize = 5;
		} else if (file.type.includes('video')) {
			maxSize = 16;
		} else if (file.type.includes('application/pdf')) {
			maxSize = 100;
		}
	}

	if (maxSize !== undefined) {
		if (typeof maxSize === 'function') {
			if (!maxSize({ size: mbSize, file })) {
				Alert.error(t('file-is-too-large'));
				return;
			}
		} else if (mbSize > maxSize) {
			Alert.error(t('file-is-too-large-with-max', { size: maxSize }));
			return;
		}
	}

	if (props.uploadType === 'firestore') {
		try {
			loading.value = true;
			const { filePath } = await uploadToFireStore(file, path);

			emit('update:modelValue', filePath);
			emit('data', { fileName: file.name });
			fileName.value = file.name;
		} finally {
			loading.value = false;
		}
	} else if (props.uploadType === 's3') {
		try {
			loading.value = true;
			const { filePath, shortPath } = await uploadToS3({ file });
			if (props.shortPath) {
				emit('update:modelValue', shortPath);
			} else {
				emit('update:modelValue', filePath);
			}
			emit('data', { fileName: file.name });
			fileName.value = file.name;
		} finally {
			loading.value = false;
		}
	} else if (props.uploadType === 'without-upload') {
		emit('update:modelValue', file);
		emit('data', { fileName: file.name });
		fileName.value = file.name;
	}
};

const reset = () => {
	fileName.value = null;
	emit('update:modelValue', undefined);
	emit('data', undefined);
};

const handleDrop = (e) => {
	setInactive();

	if (e.dataTransfer.files) handleUploadFile(e.dataTransfer.files[0]);
};

watch(
	() => props.modelValue,
	(newVal) => {
		fileName.value = newVal;
	},
);
</script>

<style lang="scss" scoped></style>
